import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Search, ArrowUpDown, Filter, MoreHorizontal, Edit, Trash2, 
  Eye, Copy, ExternalLink, ChevronLeft, ChevronRight, FileText,
  Calendar, User, Globe, Archive, Folder, FolderPlus, FolderOpen, X, MoveRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'Recently Updated' },
  { value: 'created_at', label: 'Recently Created' },
  { value: 'title', label: 'Title (A-Z)' },
  { value: 'slug', label: 'Slug (A-Z)' },
  { value: 'author_name', label: 'Author (A-Z)' },
];

const PAGE_SIZES = [10, 20, 50, 100];

const FOLDER_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#3b82f6', // Blue
  '#64748b', // Slate
];

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [allPages, setAllPages] = useState([]); // Unfiltered pages for counts
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [folderFilter, setFolderFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  
  // Folder state
  const [folders, setFolders] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#6366f1');
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await api.get('/page-folders');
      setFolders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  }, []);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/pages');
      let filteredPages = response.data;
      setAllPages(response.data); // Store unfiltered for counts
      
      // Filter by folder
      if (folderFilter !== 'all') {
        if (folderFilter === 'none') {
          filteredPages = filteredPages.filter(p => !p.folder_id);
        } else {
          filteredPages = filteredPages.filter(p => p.folder_id === folderFilter);
        }
      }
      
      // Filter by search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredPages = filteredPages.filter(p => 
          (p.title || '').toLowerCase().includes(term) ||
          p.slug.toLowerCase().includes(term) ||
          (p.author_name || '').toLowerCase().includes(term)
        );
      }
      
      // Filter by status
      if (statusFilter !== 'all') {
        filteredPages = filteredPages.filter(p => p.status === statusFilter);
      }
      
      // Filter by author
      if (authorFilter !== 'all') {
        filteredPages = filteredPages.filter(p => (p.author_name || 'Unknown') === authorFilter);
      }
      
      // Sort
      filteredPages.sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
          case 'title':
            aVal = (a.title || '').toLowerCase();
            bVal = (b.title || '').toLowerCase();
            break;
          case 'slug':
            aVal = a.slug.toLowerCase();
            bVal = b.slug.toLowerCase();
            break;
          case 'author_name':
            aVal = (a.author_name || '').toLowerCase();
            bVal = (b.author_name || '').toLowerCase();
            break;
          case 'created_at':
            aVal = new Date(a.created_at);
            bVal = new Date(b.created_at);
            break;
          case 'updated_at':
          default:
            aVal = new Date(a.updated_at);
            bVal = new Date(b.updated_at);
        }
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setTotal(filteredPages.length);
      
      // Paginate
      const start = (page - 1) * perPage;
      const paginatedPages = filteredPages.slice(start, start + perPage);
      
      setPages(paginatedPages);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, folderFilter, authorFilter, sortBy, sortOrder, page, perPage]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, folderFilter, authorFilter, sortBy, sortOrder, perPage]);

  const handleDelete = async () => {
    if (!selectedPage) return;
    try {
      await api.delete(`/pages/${selectedPage.page_id}`);
      toast.success('Page deleted successfully');
      setShowDeleteModal(false);
      setSelectedPage(null);
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const handleDuplicate = async (pageData) => {
    try {
      const newPage = {
        title: `${pageData.title || 'Untitled'} (Copy)`,
        slug: `${pageData.slug}-copy-${Date.now()}`,
        blocks: pageData.blocks,
        status: 'draft',
        pageWidth: pageData.pageWidth,
        theme: pageData.theme,
        folder_id: pageData.folder_id
      };
      await api.post('/pages', newPage);
      toast.success('Page duplicated successfully');
      fetchPages();
    } catch (error) {
      toast.error('Failed to duplicate page');
    }
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;
    try {
      await api.post('/page-folders', { name: folderName, color: folderColor });
      toast.success('Folder created successfully');
      setShowFolderModal(false);
      setFolderName('');
      setFolderColor('#6366f1');
      fetchFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleUpdateFolder = async () => {
    if (!folderName.trim() || !editingFolder) return;
    try {
      await api.put(`/page-folders/${editingFolder.folder_id}`, { name: folderName, color: folderColor });
      toast.success('Folder updated successfully');
      setShowFolderModal(false);
      setEditingFolder(null);
      setFolderName('');
      setFolderColor('#6366f1');
      fetchFolders();
    } catch (error) {
      toast.error('Failed to update folder');
    }
  };

  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;
    try {
      await api.delete(`/page-folders/${selectedFolder.folder_id}`);
      toast.success('Folder deleted successfully');
      setShowDeleteFolderModal(false);
      setSelectedFolder(null);
      if (folderFilter === selectedFolder.folder_id) {
        setFolderFilter('all');
      }
      fetchFolders();
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveToFolder = async (pageData, folderId) => {
    try {
      await api.put(`/pages/${pageData.page_id}/move-to-folder`, null, {
        params: { folder_id: folderId || '' }
      });
      toast.success(`Page moved ${folderId ? 'to folder' : 'out of folder'}`);
      fetchPages();
      fetchFolders();
    } catch (error) {
      toast.error('Failed to move page');
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || '#6366f1');
    setShowFolderModal(true);
  };

  const openNewFolder = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderColor('#6366f1');
    setShowFolderModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      published: 'bg-green-100 text-green-700 border-green-200',
      draft: 'bg-amber-100 text-amber-700 border-amber-200',
      archived: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return variants[status] || variants.draft;
  };

  const getFolderForPage = (pageData) => {
    if (!pageData.folder_id) return null;
    return folders.find(f => f.folder_id === pageData.folder_id);
  };

  const getUnfiledCount = () => {
    return allPages.filter(p => !p.folder_id).length;
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="pages-list-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="text-indigo-600" size={32} />
                Pages
              </h1>
              <p className="text-slate-500 mt-1">Manage your website pages</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={openNewFolder} className="gap-2">
                <FolderPlus size={18} />
                New Folder
              </Button>
              <Link to="/pages/new">
                <Button className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="create-page-button">
                  <Plus size={18} />
                  Create Page
                </Button>
              </Link>
            </div>
          </div>

          {/* Folders Bar */}
          {folders.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-500 mr-2">Folders:</span>
                <button
                  onClick={() => setFolderFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    folderFilter === 'all' 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Folder size={14} />
                  All Pages ({allPages.length})
                </button>
                <button
                  onClick={() => setFolderFilter('none')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    folderFilter === 'none' 
                      ? 'bg-slate-200 text-slate-800' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <FileText size={14} />
                  Unfiled ({getUnfiledCount()})
                </button>
                {folders.map(folder => (
                  <div key={folder.folder_id} className="relative group">
                    <button
                      onClick={() => setFolderFilter(folder.folder_id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        folderFilter === folder.folder_id 
                          ? 'ring-2 ring-offset-1' 
                          : 'hover:opacity-80'
                      }`}
                      style={{ 
                        backgroundColor: `${folder.color}20`, 
                        color: folder.color,
                        ringColor: folder.color 
                      }}
                    >
                      <FolderOpen size={14} />
                      {folder.name} ({folder.page_count || 0})
                    </button>
                    <div className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded-full bg-white shadow-sm border border-slate-200 hover:bg-slate-50">
                            <MoreHorizontal size={12} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditFolder(folder)}>
                            <Edit size={14} className="mr-2" />
                            Edit Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => { setSelectedFolder(folder); setShowDeleteFolderModal(true); }}
                            className="text-red-600"
                          >
                            <Trash2 size={14} className="mr-2" />
                            Delete Folder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-64">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search pages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <ArrowUpDown size={16} className="mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Author Filter */}
              <Select value={authorFilter} onValueChange={setAuthorFilter}>
                <SelectTrigger className="w-44" data-testid="author-filter">
                  <User size={16} className="mr-2" />
                  <SelectValue placeholder="Author" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Authors</SelectItem>
                  {[...new Set(allPages.map(p => p.author_name || 'Unknown'))].sort().map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort Order */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
              </Button>
            </div>
          </div>

          {/* Pages Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-slate-500">Loading pages...</div>
            ) : pages.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-1">No pages found</h3>
                <p className="text-slate-500 mb-4">
                  {searchTerm || statusFilter !== 'all' || folderFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first page to get started'}
                </p>
                {!searchTerm && statusFilter === 'all' && folderFilter === 'all' && (
                  <Link to="/pages/new">
                    <Button className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus size={18} className="mr-2" />
                      Create Page
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Page</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Folder</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Author</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Updated</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pages.map((pageData) => {
                      const folder = getFolderForPage(pageData);
                      return (
                        <tr key={pageData.page_id} className="hover:bg-slate-50 transition-colors" data-testid={`page-row-${pageData.page_id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                                <FileText size={20} />
                              </div>
                              <div>
                                <Link 
                                  to={`/pages/${pageData.page_id}`}
                                  className="font-medium text-slate-900 hover:text-indigo-600 transition-colors"
                                >
                                  {pageData.title || 'Untitled'}
                                </Link>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm text-slate-500">/{pageData.slug}</span>
                                  {pageData.isHomepage && (
                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">Home</span>
                                  )}
                                  {pageData.isPopup && (
                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">Popup</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {folder ? (
                              <span 
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium"
                                style={{ backgroundColor: `${folder.color}20`, color: folder.color }}
                              >
                                <Folder size={12} />
                                {folder.name}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusBadge(pageData.status)}`}>
                              {pageData.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <User size={14} />
                              {pageData.author_name || 'Unknown'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar size={14} />
                              {new Date(pageData.updated_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`page-actions-${pageData.page_id}`}>
                                  <MoreHorizontal size={18} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/pages/${pageData.page_id}`} className="flex items-center">
                                    <Edit size={14} className="mr-2" />
                                    Edit
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <a href={`/preview/${pageData.preview_token}`} target="_blank" rel="noopener noreferrer" className="flex items-center">
                                    <Eye size={14} className="mr-2" />
                                    Preview
                                  </a>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(pageData)}>
                                  <Copy size={14} className="mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                
                                {/* Move to Folder submenu */}
                                <DropdownMenuSeparator />
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <MoveRight size={14} className="mr-2" />
                                    Move to Folder
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem onClick={() => handleMoveToFolder(pageData, null)}>
                                      <X size={14} className="mr-2" />
                                      No Folder
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {folders.map(f => (
                                      <DropdownMenuItem 
                                        key={f.folder_id} 
                                        onClick={() => handleMoveToFolder(pageData, f.folder_id)}
                                        disabled={pageData.folder_id === f.folder_id}
                                      >
                                        <Folder size={14} className="mr-2" style={{ color: f.color }} />
                                        {f.name}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedPage(pageData); setShowDeleteModal(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 size={14} className="mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} pages
                    </span>
                    <Select value={perPage.toString()} onValueChange={(v) => setPerPage(parseInt(v))}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map(size => (
                          <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Page Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Page</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedPage?.title || 'Untitled'}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Modal (Create/Edit) */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFolder ? 'Edit Folder' : 'Create New Folder'}</DialogTitle>
            <DialogDescription>
              {editingFolder ? 'Update the folder name and color.' : 'Create a folder to organize your pages. Folders do not affect page URLs.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Folder Name</label>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="e.g., Blog Posts, Products, Events"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Color</label>
              <div className="flex gap-2 flex-wrap">
                {FOLDER_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setFolderColor(color)}
                    className={`w-8 h-8 rounded-full transition-transform ${folderColor === color ? 'ring-2 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: color, ringColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 p-3 bg-slate-50 rounded-lg">
              <Folder size={20} style={{ color: folderColor }} />
              <span className="font-medium" style={{ color: folderColor }}>{folderName || 'Folder Name'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderModal(false)}>Cancel</Button>
            <Button onClick={editingFolder ? handleUpdateFolder : handleCreateFolder} disabled={!folderName.trim()}>
              {editingFolder ? 'Save Changes' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Modal */}
      <Dialog open={showDeleteFolderModal} onOpenChange={setShowDeleteFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the folder "{selectedFolder?.name}"? 
              Pages in this folder will be moved to "Unfiled". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteFolderModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteFolder}>Delete Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pages;
